/**
 * Build-planner derivations — pure functions over the board model.
 *
 * Encodes the five planning principles:
 *  1. Today        — what to implement now (ready = all prerequisites done).
 *  2. By week      — order by leverage → critical/MVP-blocking → title.
 *  3. Up/downstream — prerequisite + dependent chains per task.
 *  4. Due dates    — each feature has a due_date inside its workstream window.
 *  5. Milestone health — on-track vs at-risk, derived from due dates + cascade.
 */

import type {
  BoardFeatureDependencyRow,
  BoardFeatureRow,
  DeliveryStatus,
  MilestoneRow,
} from "@/infrastructure/supabase/database.types";

const DAY_MS = 86_400_000;

export interface PlannerGraph {
  byId: Map<string, BoardFeatureRow>;
  /** prereqs.get(id) = features this one depends on (must be done first). */
  prereqs: Map<string, string[]>;
  /** dependents.get(id) = features that depend on this one (it unblocks them). */
  dependents: Map<string, string[]>;
  /** transitive downstream count — the "leverage" of a feature. */
  leverage: Map<string, number>;
}

export function buildPlannerGraph(
  features: BoardFeatureRow[],
  deps: BoardFeatureDependencyRow[]
): PlannerGraph {
  const byId = new Map(features.map((f) => [f.id, f]));
  const prereqs = new Map<string, string[]>();
  const dependents = new Map<string, string[]>();
  for (const f of features) {
    prereqs.set(f.id, []);
    dependents.set(f.id, []);
  }
  for (const d of deps) {
    if (byId.has(d.feature_id) && byId.has(d.depends_on_feature_id)) {
      prereqs.get(d.feature_id)!.push(d.depends_on_feature_id);
      dependents.get(d.depends_on_feature_id)!.push(d.feature_id);
    }
  }
  const leverage = new Map<string, number>();
  const transitiveCount = (id: string): number => {
    const seen = new Set<string>();
    const stack = [...(dependents.get(id) ?? [])];
    while (stack.length) {
      const cur = stack.pop()!;
      if (seen.has(cur)) continue;
      seen.add(cur);
      for (const d of dependents.get(cur) ?? []) stack.push(d);
    }
    return seen.size;
  };
  for (const f of features) leverage.set(f.id, transitiveCount(f.id));
  return { byId, prereqs, dependents, leverage };
}

export const isDone = (f: BoardFeatureRow): boolean => f.delivery_status === "done";
export const isActive = (f: BoardFeatureRow): boolean =>
  f.delivery_status === "in-progress" || f.delivery_status === "in-review";

/** Ready = not done/active/blocked and every prerequisite is done. */
export function isReady(f: BoardFeatureRow, g: PlannerGraph): boolean {
  if (isDone(f) || isActive(f) || f.delivery_status === "blocked") return false;
  return (g.prereqs.get(f.id) ?? []).every((p) => {
    const pf = g.byId.get(p);
    return pf ? isDone(pf) : true;
  });
}

const dueMs = (f: BoardFeatureRow): number | null =>
  f.due_date ? new Date(f.due_date + "T00:00:00").getTime() : null;

export function isOverdue(f: BoardFeatureRow, todayMs: number): boolean {
  const d = dueMs(f);
  return !isDone(f) && d !== null && todayMs > d;
}

export function overdueDays(f: BoardFeatureRow, todayMs: number): number {
  const d = dueMs(f);
  if (d === null) return 0;
  return Math.max(0, Math.round((todayMs - d) / DAY_MS));
}

/**
 * At-risk cascades down the dependency graph: a not-yet-late task is at-risk if
 * a prerequisite is overdue, itself at-risk, or due on/after this task (so it
 * cannot realistically finish in time). Memoized; the graph is a DAG.
 */
export function makeAtRisk(g: PlannerGraph, todayMs: number): (f: BoardFeatureRow) => boolean {
  const cache = new Map<string, boolean>();
  const visiting = new Set<string>();
  const compute = (id: string): boolean => {
    if (cache.has(id)) return cache.get(id)!;
    if (visiting.has(id)) return false; // cycle guard
    visiting.add(id);
    const f = g.byId.get(id)!;
    let risk = false;
    if (!isDone(f) && !isOverdue(f, todayMs)) {
      const myDue = dueMs(f);
      risk = (g.prereqs.get(id) ?? []).some((p) => {
        const pf = g.byId.get(p);
        if (!pf || isDone(pf)) return false;
        const pDue = dueMs(pf);
        return (
          isOverdue(pf, todayMs) || compute(p) || (myDue !== null && pDue !== null && pDue >= myDue)
        );
      });
    }
    visiting.delete(id);
    cache.set(id, risk);
    return risk;
  };
  return (f) => compute(f.id);
}

export type TaskHealth = "done" | "overdue" | "at-risk" | "ready" | "in-progress" | "waiting";

/** Order within a shared week: leverage desc → critical/MVP-blocking → title. */
export function compareForOrder(g: PlannerGraph) {
  return (a: BoardFeatureRow, b: BoardFeatureRow): number =>
    (g.leverage.get(b.id) ?? 0) - (g.leverage.get(a.id) ?? 0) ||
    (b.mvp_blocking ? 1 : 0) - (a.mvp_blocking ? 1 : 0) ||
    a.title.localeCompare(b.title);
}

// ---- week axis -----------------------------------------------------------

export function projectStartMs(milestones: MilestoneRow[]): number {
  const dates = milestones.map((m) => m.target_date).filter((d): d is string => Boolean(d));
  const min = dates.length ? dates.sort()[0] : new Date().toISOString().slice(0, 10);
  return new Date(min + "T00:00:00").getTime();
}

export function weekOf(dateStr: string | null, startMs: number): number | null {
  if (!dateStr) return null;
  const t = new Date(dateStr + "T00:00:00").getTime();
  return Math.floor((t - startMs) / (7 * DAY_MS)) + 1;
}

export function weekStartDate(week: number, startMs: number): Date {
  return new Date(startMs + (week - 1) * 7 * DAY_MS);
}

export function fmtShort(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

// ---- milestone health ----------------------------------------------------

export type MilestoneStatus = "done" | "ontrack" | "at-risk" | "breached";

export interface MilestoneHealth {
  milestone: MilestoneRow;
  total: number;
  done: number;
  overdue: number;
  atRisk: number;
  days: number | null; // days until gate (negative = past)
  status: MilestoneStatus;
}

export function milestoneHealth(
  milestone: MilestoneRow,
  features: BoardFeatureRow[],
  todayMs: number,
  atRisk: (f: BoardFeatureRow) => boolean
): MilestoneHealth {
  const fs = features.filter((f) => f.milestone_key === milestone.key);
  const done = fs.filter(isDone).length;
  const overdue = fs.filter((f) => isOverdue(f, todayMs)).length;
  const ar = fs.filter((f) => atRisk(f) && !isOverdue(f, todayMs)).length;
  const gate = milestone.target_date
    ? new Date(milestone.target_date + "T00:00:00").getTime()
    : null;
  const days = gate === null ? null : Math.round((gate - todayMs) / DAY_MS);
  let status: MilestoneStatus;
  if (fs.length > 0 && done === fs.length) status = "done";
  else if (gate !== null && todayMs > gate) status = "breached";
  else if (overdue > 0 || ar > 0) status = "at-risk";
  else status = "ontrack";
  return { milestone, total: fs.length, done, overdue, atRisk: ar, days, status };
}

export const MILESTONE_STATUS_LABEL: Record<MilestoneStatus, string> = {
  done: "Done",
  ontrack: "On track",
  "at-risk": "At risk",
  breached: "Breached",
};

/** CSS color var per milestone status (mission-control tokens). */
export const MILESTONE_STATUS_COLOR: Record<MilestoneStatus, string> = {
  done: "var(--rag-green)",
  ontrack: "var(--primary)",
  "at-risk": "var(--rag-amber)",
  breached: "var(--rag-red)",
};

export type { DeliveryStatus };
